import { Private } from "../../components/Privacy";
import { money } from "../../lib/finance";
import type { InsightMonth } from "./insight-types";
import { monthLabel } from "./insight-period";
import ChartFrame from "./ChartFrame";
import { useChartWidth } from "./use-chart-width";

const HEIGHT = 230, LEFT = 48, TOP = 18, BASE = 188;

export default function CashFlowChart({ rows, currency, onOpenMonth }: {
  rows: InsightMonth[]; currency: string; onOpenMonth: (month: string) => void;
}) {
  const { ref, width } = useChartWidth(680);
  const peak = Math.max(...rows.flatMap((r) => [r.income ?? 0, r.spending ?? 0]), 1);
  const group = (width - LEFT - 12) / rows.length;
  const bar = Math.min(22, group * 0.28);
  const y = (value: number) => BASE - value / peak * (BASE - TOP);
  const incomplete = rows.some((r) => r.income === null || r.spending === null);
  return (
    <section ref={ref} className="panel insight-panel insight-cashflow">
      <div className="section-heading"><div><h2>Money in, money out</h2><p>Monthly income and spending · {currency}</p></div></div>
      <div className="insight-legend"><span><i className="income" /> Income</span><span><i className="spending" /> Spending</span></div>
      <ChartFrame>
        <svg viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label="Monthly income and spending bar chart. Exact figures are available below.">
          {[0, 0.5, 1].map((step) => (
            <g key={step}><line x1={LEFT} x2={width - 12} y1={y(peak * step)} y2={y(peak * step)} className="insight-grid-line" />
              <text x={LEFT - 9} y={y(peak * step) + 4} textAnchor="end" className="insight-axis">{new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(peak * step)}</text></g>
          ))}
          {rows.map((row, i) => {
            const center = LEFT + group * (i + 0.5);
            return <g key={row.month}>
              {(["income", "spending"] as const).map((key, n) => {
                const value = row[key];
                return value === null ? <text key={key} x={center + (n ? bar / 2 + 2 : -bar / 2 - 2)} y={BASE - 5} textAnchor="middle" className="insight-axis">?</text> :
                  <rect key={key} x={center + (n ? 2 : -bar - 2)} y={y(value)} width={bar} height={BASE - y(value)} rx="3" className={`insight-bar ${key}`}>
                    <title>{monthLabel(row.month)} · {key === "income" ? "Income" : "Spending"}: {money(value, currency)}</title>
                  </rect>;
              })}
              {(width >= 450 || rows.length < 12 || i % 2 === 0) && <text x={center} y={BASE + 23} textAnchor="middle" className="insight-axis">{monthLabel(row.month, true).split(" ")[0]}</text>}
            </g>;
          })}
        </svg>
      </ChartFrame>
      {incomplete && <p className="insight-note">? means a total is unavailable because an exchange rate is missing.</p>}
      <details className="insight-figures"><summary>View monthly figures</summary>
        <div className="insight-table-wrap"><table>
          <caption className="sr-only">Monthly income, spending and income remaining after spending in {currency}</caption>
          <thead><tr><th scope="col">Month</th><th scope="col">Income</th><th scope="col">Spending</th><th scope="col">After spending</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={row.month}>
            <th scope="row"><button type="button" onClick={() => onOpenMonth(row.month)} aria-label={`View ${monthLabel(row.month)} transactions`}>{monthLabel(row.month, true)}</button></th>
            <td><Private>{money(row.income, currency)}</Private></td><td><Private>{money(row.spending, currency)}</Private></td><td><Private>{money(row.remaining, currency)}</Private></td>
          </tr>)}</tbody>
        </table></div>
      </details>
      <p className="insight-note">Empty months mean no entries recorded. The current month includes only dates through today.</p>
    </section>
  );
}
